import { useState } from 'react';
import { getInitials } from '../../utils/bookingFormat';
import StylistWorkModal from './StylistWorkModal';
import type { PublicBookingStaff } from '../../types/publicBooking';

/**
 * `undefined` means the customer has not chosen yet, `null` means they picked
 * "Any available" — a distinction the step-1 guard depends on, since "no
 * preference" is a valid answer but "not answered" is not.
 */
export type StylistChoice = number | null | undefined;

interface StylistPickerProps {
	staff: PublicBookingStaff[];
	value: StylistChoice;
	onChange: (value: number | null) => void;
	/** Called when "Book with This Stylist" is used from inside the work modal. */
	onBookFromModal?: (id: number) => void;
}

// Warm portrait backgrounds, cycled so adjacent cards differ. Only shown for
// stylists without a portfolio photo.
const CARD_GRADIENTS = [
	'linear-gradient(145deg,#8b4513,#c4762a)',
	'linear-gradient(145deg,#5c3317,#9b6845)',
	'linear-gradient(145deg,#6b3a20,#b07040)',
	'linear-gradient(145deg,#7a3e28,#bf7048)',
	'linear-gradient(145deg,#4a2810,#8b5e3c)',
	'linear-gradient(145deg,#3d1f0a,#7a4f28)',
];

/** Human label for a grade code, e.g. "MASTER" → "Master". */
const formatGrade = (grade: string): string =>
	grade.charAt(0).toUpperCase() + grade.slice(1).toLowerCase();

/**
 * Step 1 of the paid booking flow: pick a stylist, or let the salon assign one.
 *
 * Each card doubles as a credential — how many appointments the stylist has
 * completed and what they do most — because for a customer choosing between
 * names they do not know, that is the whole decision.
 */
const StylistPicker = ({ staff, value, onChange, onBookFromModal }: StylistPickerProps) => {
	const [viewingWorkOf, setViewingWorkOf] = useState<number | null>(null);
	const viewing = staff.find(member => member.id === viewingWorkOf) ?? null;
	const viewingIndex = staff.findIndex(member => member.id === viewingWorkOf);

	return (
		<>
			<button
				type="button"
				className={`pb-stylist__any${value === null ? ' is-selected' : ''}`}
				onClick={() => onChange(null)}
			>
				<span className="pb-stylist__any-icon">
					<svg
						width="20"
						height="20"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						strokeWidth="1.8"
						strokeLinecap="round"
						strokeLinejoin="round"
					>
						<path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
						<circle cx="9" cy="7" r="4" />
						<path d="M23 21v-2a4 4 0 0 0-3-3.87" />
						<path d="M16 3.13a4 4 0 0 1 0 7.75" />
					</svg>
				</span>
				<span className="pb-stylist__any-info">
					<span className="pb-stylist__any-name">Any Available Stylist</span>
					<span className="pb-stylist__any-desc">
						We&apos;ll assign the best available stylist for your appointment
					</span>
				</span>
			</button>

			<div className="pb-stylist__grid">
				{staff.map((member, index) => {
					const isSelected = value === member.id;
					const photo = member.portfolioImageUrls?.[0];
					const topServices = (member.topServices ?? []).slice(0, 2);
					// The card is a plain div, not a button: it holds two buttons of
					// its own, and a button inside a button is invalid markup that
					// browsers and screen readers both handle badly. Clicking the
					// card still selects — "Book" is the accessible equivalent.
					return (
						<div
							key={member.id}
							className={`pb-stylist__card${isSelected ? ' is-selected' : ''}`}
							onClick={() => onChange(member.id)}
						>
							<div
								className="pb-stylist__photo"
								style={{
									background: CARD_GRADIENTS[index % CARD_GRADIENTS.length],
								}}
							>
								{photo ? (
									<img src={photo} alt="" loading="lazy" />
								) : (
									<div className="pb-stylist__initials">
										{getInitials(member.name)}
									</div>
								)}
								{isSelected && <div className="pb-stylist__badge">✓</div>}
							</div>

							<div className="pb-stylist__body">
								<div className="pb-stylist__name">{member.name}</div>
								{(member.grade || member.role) && (
									<div className="pb-stylist__grade">
										{member.grade ? formatGrade(member.grade) : member.role}
									</div>
								)}

								<div className="pb-stylist__stats">
									<div className="pb-stylist__total">
										<span className="pb-stylist__total-num">
											{member.completedCount ?? 0}
										</span>
										<span className="pb-stylist__total-label">
											appointments
										</span>
									</div>
									<div className="pb-stylist__svc-label">Top services</div>
									{topServices.length > 0 ? (
										topServices.map(service => (
											<div key={service.name} className="pb-stylist__svc">
												<span
													className="pb-stylist__svc-name"
													title={service.name}
												>
													{service.name}
												</span>
												<span className="pb-stylist__svc-count">
													{service.count}
												</span>
											</div>
										))
									) : (
										<div className="pb-stylist__svc-empty">
											No services logged yet
										</div>
									)}
								</div>

								<div className="pb-stylist__actions">
									<button
										type="button"
										className="pb-stylist__work-btn"
										onClick={event => {
											event.stopPropagation();
											setViewingWorkOf(member.id);
										}}
									>
										👁 See Work
									</button>
									<button
										type="button"
										className="pb-stylist__book"
										aria-pressed={isSelected}
										onClick={event => {
											event.stopPropagation();
											onChange(member.id);
										}}
									>
										{isSelected ? '✓ Selected' : 'Book'}
									</button>
								</div>
							</div>
						</div>
					);
				})}
			</div>

			{viewing && (
				<StylistWorkModal
					stylist={viewing}
					gradient={CARD_GRADIENTS[viewingIndex % CARD_GRADIENTS.length]}
					onClose={() => setViewingWorkOf(null)}
					onBook={() => {
						setViewingWorkOf(null);
						onChange(viewing.id);
						onBookFromModal?.(viewing.id);
					}}
				/>
			)}
		</>
	);
};

export default StylistPicker;
